import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { passwordService } from "../../../services/auth/password.service";
import { AxiosError } from "axios";

interface ApiErrorResponse {
  success: boolean;
  message: string;
}

export const BrandForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shops, setShops] = useState<
    Array<{ sessionId: string; shop: string; companyName: string }>
  >([]);
  const [showShopSelection, setShowShopSelection] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await passwordService.forgotPassword(email, "brand");

      // Check if multiple brands found and shop selection is required
      if (response.requiresSelection && response.shops) {
        setShops(response.shops);
        setShowShopSelection(true);
      } else {
        // Single brand or success, navigate to OTP verification
        navigate(`/brand/auth/verify-otp?email=${encodeURIComponent(email)}`);
      }
    } catch (err) {
      if (err instanceof AxiosError) {
        const errorData = err.response?.data as ApiErrorResponse;
        setError(errorData?.message || "Failed to send password reset email");
      } else {
        setError(
          err instanceof Error ? err.message : "An unexpected error occurred"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleShopSelection = async (sessionId: string) => {
    setError(null);
    setIsLoading(true);

    try {
      await passwordService.forgotPasswordForShop(email, sessionId, "brand");
      navigate(
        `/brand/auth/verify-otp?email=${encodeURIComponent(
          email
        )}&sessionId=${encodeURIComponent(sessionId)}`
      );
    } catch (err) {
      if (err instanceof AxiosError) {
        const errorData = err.response?.data as ApiErrorResponse;
        setError(errorData?.message || "Failed to send password reset email");
      } else {
        setError(
          err instanceof Error ? err.message : "An unexpected error occurred"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderError = () =>
    error && (
      <div className="mb-4 p-3 bg-destructive/10 text-destructive rounded-lg">{error}</div>
    );

  return (
    <div className=" md:m-auto mx-auto w-[98%] md:w-max pt-4 md:pt-0 h-[88%] md:h-max">
      <div className="  h-full mx-auto flex items-center justify-center  md:px-5 md:py-7 py-6 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100]  ">

        <div className="w-full flex items-center justify-center px-6 lg:px-16 xl:px-24">
          <div className="w-full max-w-[440px]">
            {renderError()}
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-foreground">
                Reset your password
              </h1>
              <p className="mt-2 text-muted-foreground">
                {showShopSelection
                  ? "Multiple accounts found. Please select which shop you want to reset the password for."
                  : "Enter your email address and we'll send you a short code to verify your identity."}
              </p>
            </div>

            {showShopSelection ? (
              <div className="space-y-4">
                {shops.map((shop) => (
                  <button
                    key={shop.sessionId}
                    type="button"
                    onClick={() => handleShopSelection(shop.sessionId)}
                    disabled={isLoading}
                    className="w-full text-left p-4 border border-border rounded-lg hover:bg-accent hover:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 transition-colors"
                  >
                    <div className="font-medium text-foreground">
                      {shop.companyName}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {shop.shop}
                    </div>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setShowShopSelection(false);
                    setShops([]);
                  }}
                  className="w-full text-center py-2 text-sm text-brand-600 hover:text-brand-700"
                >
                  Back
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-muted-foreground"
                  >
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="mt-1 block w-full px-3 py-2 border border-border rounded-lg shadow-sm focus:ring-brand-500 focus:border-brand-500"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin mr-2" />
                  ) : null}
                  <span>Send Code</span>
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <Link
                to="/auth/login"
                className="text-sm text-brand-600 hover:text-brand-500 font-medium"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
